import { useLocation } from "@reach/router"
import { Layout as AntLayout } from "antd"
// PER-COMPONENT ANTD STYLES, NOT `antd/dist/antd.css`.
//
// The full stylesheet is 580 KB and ships to every page. Only twelve antd
// components are imported anywhere in src/, and antd v4 publishes a compiled
// per-component entrypoint at `antd/es/<name>/style/css` — precompiled CSS, so
// this needs no less-loader, no babel-plugin-import and no new dependency.
//
// Each entrypoint pulls in `antd/es/style/default.css` itself, which carries the
// global reset (`html, body`, `box-sizing`, the normalize block) and the
// `.anticon` base rules that @ant-design/icons depends on. Dropping the bundle
// therefore does not drop the reset.
//
// KEPT AT LINE 3, where the bundle import was. Load order decides the cascade
// against `../styles/layout.scss` and every page stylesheet below it; moving
// these later would let antd start winning overrides it currently loses.
//
// TWO OF THESE RENDER NOTHING IN STATIC HTML AND MUST NOT BE PRUNED ON THAT
// BASIS: `modal` (InfoModal, InfoJobModal) and `message` (Footer,
// services/contact, and the three form components) only ever appear after an
// interaction. `grid` covers Row and Col.
//
// Add a line here whenever a new antd component is imported anywhere in src/.
import "antd/es/button/style/css"
import "antd/es/collapse/style/css"
import "antd/es/form/style/css"
import "antd/es/grid/style/css"
import "antd/es/input/style/css"
import "antd/es/layout/style/css"
import "antd/es/message/style/css"
import "antd/es/modal/style/css"
import "antd/es/select/style/css"
import "antd/es/spin/style/css"
import "antd/es/typography/style/css"
import "antd/es/upload/style/css"
import { graphql, useStaticQuery } from "gatsby"
import React, { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { BsRobot } from "react-icons/bs"
import { RxCross2 } from "react-icons/rx"
import { RecoilRoot } from "recoil"
import "../styles/layout.scss"
import Chat from "./Chat"
import Footer from "./Footer"
import TechMarquee from "./TechMarquee"
import Header from "./Header"

// Persisted dismissal for the chat prompt. Kept outside the component so the
// key is declared once and both readers use the same string.
const CHAT_PROMPT_KEY = "vm.chatPrompt.dismissed"

const dismissedChatPrompt = () => {
  try {
    return window.localStorage.getItem(CHAT_PROMPT_KEY) === "1"
  } catch (e) {
    return false // storage blocked: fall back to showing it
  }
}

const rememberChatPromptDismissed = () => {
  try {
    window.localStorage.setItem(CHAT_PROMPT_KEY, "1")
  } catch (e) {
    /* storage blocked — the prompt will return next page, which is the old
       behaviour and is better than throwing */
  }
}

const Layout = ({ children, className, data, style, pageTitle, descrip, canonicalOverride }) => {
  const { Content } = AntLayout
  const location = useLocation()

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)
  const siteUrl = (site?.siteMetadata?.siteUrl || "https://www.visiomate.com").replace(/\/$/, "")

  // Build a clean canonical that matches the site's true canonical form
  // (enforced by .htaccess): https + www + NO trailing slash, except root.
  // Strips query string and hash by using pathname, so ?trk=/?topic= tracking
  // variants all collapse to the clean URL. Pages that must point at a
  // DIFFERENT canonical (e.g. duplicate form pages) pass canonicalOverride.
  const rawPath = location.pathname || "/"
  const cleanPath = rawPath === "/" ? "/" : rawPath.replace(/\/+$/, "")
  const canonicalUrl = canonicalOverride || `${siteUrl}${cleanPath}`
  const [cookieModal, setCookieModal] = useState(true)
  const [user, setUser] = useState(null)
  const [botChat, setBotChat] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [analyticsOn, setAnalyticsOn] = useState(true)
  const [marketingOn, setMarketingOn] = useState(false)

  /**
   * The chat prompt ("Do you have any question?") is shown once per visitor,
   * not once per page.
   *
   * Every page renders its own <Layout>, so a client-side navigation mounts a
   * NEW Layout and this state resets — which is why the prompt used to reappear
   * a second after every page change even though the visitor had closed it.
   * The dismissal is therefore persisted outside component state.
   *
   * localStorage, not sessionStorage: "it shouldn't show up again" means across
   * visits, not just this tab. Change the store to sessionStorage if it should
   * come back for a returning visitor.
   *
   * Wrapped in try/catch because localStorage throws, not returns null, in a
   * blocked-cookies or private-mode context — and an exception here would take
   * the whole Layout down with it.
   */
  useEffect(() => {
    if (dismissedChatPrompt()) return
    const timer = setTimeout(() => setShowPopup(true), 1000)
    return () => clearTimeout(timer)
  }, [])

  const acceptCookie = () => {
    setCookieModal(false)
    localStorage.setItem("user", true)
  }
  const declineCookie = () => {
    setCookieModal(false)
    localStorage.setItem("user", false)
  }
  const savePreferences = () => {
    setCookieModal(false)
    localStorage.setItem("user", JSON.stringify({ analytics: analyticsOn, marketing: marketingOn }))
  }

  useEffect(() => {
    if (window !== undefined) {
      setUser(localStorage.getItem("user"))
    }
  }, [])

  const functionCallHandler = async (call) => {
    const args = JSON.parse(call.function.arguments)
    const response = await fetch(
      `https://staging.visiomate.com/create_consultancy/ai_query`,
      {
        method: "POST",
        body: JSON.stringify(args),
      }
    )
    let data = await response.text()
    return JSON.stringify(data)
  }

  return (
    <RecoilRoot>
      <AntLayout>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={descrip} />
          <link
            rel="canonical"
            href={canonicalUrl}
            key="canonical-link"
          />
        </Helmet>
        <Header data={data} />
        
        {/* Google tag (gtag.js) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-851LTMSYQ9"></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-851LTMSYQ9');
            `,
          }}
        />

        {/* ── Cookie consent ── */}
        {cookieModal && user === null && (
          <>
            {showPreferences && (
              <div className="ck-prefs">
                <div className="ck-prefs__head">
                  <h3 className="ck-prefs__title">Cookie preferences</h3>
                  <button className="ck-prefs__close" onClick={() => setShowPreferences(false)}>✕</button>
                </div>
                <p className="ck-prefs__why-label">Why we ask</p>
                <p className="ck-prefs__why-text">Pick which categories you're OK with. You can change this anytime.</p>
                <div className="ck-prefs__rows">
                  <div className="ck-prefs__row">
                    <span className="ck-prefs__row-name">Strictly necessary</span>
                    <div className="ck-toggle ck-toggle--locked"><div className="ck-toggle__track" /></div>
                  </div>
                  <div className="ck-prefs__row">
                    <span className="ck-prefs__row-name">Analytics</span>
                    <label className="ck-toggle">
                      <input type="checkbox" checked={analyticsOn} onChange={e => setAnalyticsOn(e.target.checked)} />
                      <div className="ck-toggle__track" />
                    </label>
                  </div>
                  <div className="ck-prefs__row">
                    <span className="ck-prefs__row-name">Marketing</span>
                    <label className="ck-toggle">
                      <input type="checkbox" checked={marketingOn} onChange={e => setMarketingOn(e.target.checked)} />
                      <div className="ck-toggle__track" />
                    </label>
                  </div>
                </div>
                <div className="ck-prefs__more">
                  <p className="ck-prefs__more-title">More information</p>
                  <p className="ck-prefs__more-text">
                    For details about what each category does and how to exercise your data rights, see our{" "}
                    <a href="/privacypolicy" className="ck-prefs__more-link">Privacy Policy</a>.
                  </p>
                </div>
                <div className="ck-prefs__actions">
                  <button className="ck-btn ck-btn--primary" onClick={acceptCookie}>Accept all</button>
                  <button className="ck-btn ck-btn--dark" onClick={declineCookie}>Reject all</button>
                  <button className="ck-btn ck-btn--dark" onClick={savePreferences}>Save preferences</button>
                </div>
              </div>
            )}
            <div className="ck-banner">
              <div className="ck-banner__left">
                <h3 className="ck-banner__title">We use a few cookies.</h3>
                <p className="ck-banner__desc">
                  Some are required for the site to work. Others help us understand which pages
                  perform — only if you opt in. We don't sell your data, and you can change your mind anytime.
                </p>
                <a href="/privacypolicy" className="ck-banner__policy">Privacy Policy</a>
              </div>
              <div className="ck-banner__actions">
                <button className="ck-btn ck-btn--primary" onClick={acceptCookie}>Accept all</button>
                <button className="ck-btn ck-btn--dark" onClick={declineCookie}>Reject all</button>
                <button className="ck-btn ck-btn--outline" onClick={() => setShowPreferences(p => !p)}>
                  Manage preferences
                </button>
              </div>
            </div>
          </>
        )}
        <div
          style={{ position: "fixed", bottom: "10px", right: "15px", zIndex: 1100 }}
        >
          <div style={{ position: "relative" }}>
            {!!showPopup && <div className='opener'>
              <span>Do you have any question? 😀</span>
              <span>Visiomate's team is online</span>
              <button className='chatbtn' onClick={() => {
                setBotChat(true);
                rememberChatPromptDismissed();
              }}>Chat with Visiomate's Team</button>
              <button onClick={(e) => {
                e.preventDefault();
                setShowPopup(false);
                rememberChatPromptDismissed();
              }} className='popup-close'><RxCross2 /></button>
            </div>}
            <button
              className="accept_btn cookie_button bot-button"
              style={{ borderRadius: '50% !important' }}
              onClick={(e) => {
                e.preventDefault()
                setShowPopup(false)
                rememberChatPromptDismissed()
                setBotChat(!botChat)
              }}
            >
              <p className="botPara">
                {!botChat ? <BsRobot className="botIcon" /> : <RxCross2 className="botIcon" />}
              </p>
            </button>
            {botChat && <Chat functionCallHandler={functionCallHandler} setBotChat={setBotChat} />}
          </div>
        </div>.
        <Content className={className} style={style}>
          {children}
        </Content>
        {/* {!(
          location.pathname.includes("privacypolicy") ||
          location.pathname.includes("userdeletionpolicy")
        ) && <TalkToUs />} */}
        {/* The tech strip, sitewide.
            Immediately above the footer rather than inside it: .vf carries
            `padding: 3.5rem 5% !important`, which would inset a full-bleed band
            by 5% on both sides and push it 3.5rem down from the footer's own top
            edge. Out here it spans edge to edge and reads as attached to the
            footer, which is the point.
            Every page renders through this Layout, so this is every page. */}
        <TechMarquee />
        <Footer data={data} />
      </AntLayout>
    </RecoilRoot>
  )
}

export default Layout
